<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  onMounted,
  provide,
  ref,
  reactive,
  computed,
  ComputedRef,
  inject,
  Ref,
  watchEffect,
} from 'vue'
import { skSelectOption } from '/@src/constants'
import { useSteps } from '/@src/composable/useSteps'
import { useFormErrors, useFormValues, useValidateForm } from 'vee-validate'
import { useForm, useField } from 'vee-validate'
import { getCiselnik } from '/@src/composable/callableApi'
import { useFieldArray } from 'vee-validate'
import { getOrganizacieApi } from '/@src/composable/useApis'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import * as apisPublic from '/@src/client-public/api'
import { ZoPPMData, ZoppmPartnerSubjektu } from '/@src/client-portal'
import { getOsobyApi } from '/@src/composable/useApis'

import {
  OsobaRef,
  OrganizaciaRef,
  OsobaCreate,
  OrganizaciaCreate,
} from '/@src/client-public'

const isSectionVisible = ref(false)
const partneriData = ref()
const partneriDataTable = ref()
const isFOVisible = ref(false)
const isPOVisible = ref(false)
const PoRadioChecked = ref(false)

const FoRadioChecked = ref(false)
const isSectionShowDetailPartnerVisible = ref(false)
const isSectionAddPartnerVisible = ref(false)
const isSectionShowDetailPartnerVisiblePo = ref(false)
const isSectionSearchPartnerVisible = ref(true)
const errors = useFormErrors()
const { activeStep, nextStep, setPrevNextStep } = useSteps()
const showErrors = computed(
  () => activeStep.value === NazvyKrokov['zhrnutie'] && Object.keys(errors.value)?.length
)
const { t } = useI18n()

let modeDate = ref('')
partneriData.value = []
partneriDataTable.value = []
const toggleSection = () => {
  isSectionVisible.value = !isSectionVisible.value
  isSectionSearchPartnerVisible.value = true
  isSectionAddPartnerVisible.value = false
  isSectionShowDetailPartnerVisible.value = false
  isSectionShowDetailPartnerVisiblePo.value = false
}

const toggleSectionRadioFO = () => {
  isFOVisible.value = !isFOVisible.value
  isPOVisible.value = false
  isSectionSearchPartnerVisible.value = true
  isSectionAddPartnerVisible.value = false
  isSectionShowDetailPartnerVisible.value = false
}

const toggleSectionRadioPO = () => {
  isPOVisible.value = !isPOVisible.value
  isSectionSearchPartnerVisible.value = true
  isSectionAddPartnerVisible.value = false
  isFOVisible.value = false
  isSectionShowDetailPartnerVisible.value = false
}

import { Osoba, CiselnikBasicResponse, OsobaOrganizacie } from '/@src/client-portal'
import { useUserSession } from '/@src/stores/userSession'
import { getPortalOsobaApi } from '/@src/composable/useApis'
import AddressFieldsPartneri from '/@src/components/AddressFieldsPartneri.vue'

let ulicaSelection = ref('')
let obecSelection = ref(undefined)
let okresSelection = ref(undefined)
let krajSelection = ref(undefined)
let skPredefined = ref(true)
const pravnaForma = ref<CiselnikBasicResponse[]>([])
const datumNarodeniaForm = ref(undefined)
const menoForm = ref('')
const titulPredMenomArrayForm = ref([])
const titulZaMenomArrayForm = ref([])
const priezviskoForm = ref('')
const rodneCisloForm = ref('')
let statnaPrislusnost = ref(skSelectOption)
const aktualizovatPartnera = ref(false)
const odstranitPartnera = ref(false)
const vytvoritNovehoPartnera = ref(true)

export interface SearchValues {
  meno: string
  priezvisko: string
  rodneCislo?: string
  datumNarodenia?: Date
}

export interface StatnaPrislusnostSelect {
  id: number
  kodZdroj: string
  nazov: string
}

let statnaPrislusnostSelectValues = reactive<StatnaPrislusnostSelect>({
  id: 0,
  kodZdroj: '',
  nazov: '',
})

const userSession = useUserSession()

onMounted(async () => {
  stat.value = await getCiselnik('stat')

  titulPredMenom.value = await getCiselnik('titulpredmenom')
  titulZaMenom.value = await getCiselnik('titulzamenom')
  pravnaForma.value = await getCiselnik('pravnaforma')
})

const osobyOrganizacie = ref<OsobaOrganizacie[]>([])
const titulPredMenom = ref<CiselnikBasicResponse[]>([])
const open = ref(false)
const titulZaMenom = ref<CiselnikBasicResponse[]>([])
const stat = ref<CiselnikBasicResponse[]>([])

const state = reactive<{
  showFillError: boolean
  firstLoad: boolean
  isLoading: boolean
  searchResults: Osoba[]
}>({
  showFillError: false,
  firstLoad: false,
  isLoading: false,
  searchResults: [],
})

const statePo = reactive<{
  showFillErrorPartner: boolean
}>({
  showFillErrorPartner: false,
})

let searchCountIsEmpty = computed(() => state.searchResults.length === 0)

let searchValues = reactive<SearchValues>(
  userSession.searchValues ?? {
    meno: '',
    priezvisko: '',
    rodneCislo: '',
  }
)

const datumNarodeniaFormatted = computed(() => {
  if (searchValues.datumNarodenia) {
    const d = new Date(searchValues.datumNarodenia)
    return [d.getDate(), d.getMonth() + 1, d.getFullYear()].join('.')
  }
  return ''
})

const replaceResultsText = computed(() => {
  const l = state.searchResults.length
  const replace = { pocet: l }
  if (l === 0 || l > 4) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdenych'),
      evidovane: t('subjektOsoby.search.evidovanych'),
      osoby: t('subjektOsoby.search.osob'),
    }
  } else if (l > 1 && l < 4) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdene'),
      evidovane: t('subjektOsoby.search.evidovane'),
      osoby: t('subjektOsoby.search.osoby'),
    }
  } else if (l === 1) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdena'),
      evidovane: t('subjektOsoby.search.evidovana'),
      osoby: t('subjektOsoby.search.osoba'),
    }
  } else {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdene'),
      evidovane: t('subjektOsoby.add.evidovane'),
      osoby: t('subjektOsoby.add.osoby'),
    }
  }
})

const isInOrganizacia = (osoba) =>
  osobyOrganizacie.value?.find((o) => o?.osoba?.osobaId === osoba?.osobaId)

const { value: showDatumNarodenia, setValue: setShowDatumNarodenia } =
  useField('showDatumNarodenia')

const onSubmit = async () => {
  const formatDate = (date: string) => {
    const [y, m, d] = date.split('T')[0].split('-')
    return [y, m.padStart(2, '0'), d.padStart(2, '0')].join('-')
  }

  const searchByDate = showDatumNarodenia.value
  state.showFillError = false

  if (
    (datumNarodeniaForm.value != undefined || rodneCisloForm.value != '') &&
    menoForm.value != '' &&
    priezviskoForm.value != ''
  ) {
    searchValues.meno = menoForm.value
    searchValues.priezvisko = priezviskoForm.value
    searchValues.rodneCislo = searchByDate ? undefined : rodneCisloForm.value
    searchValues.datumNarodenia = searchByDate
      ? datumNarodeniaForm.value && formatDate(datumNarodeniaForm.value)
      : undefined

    if (!state.firstLoad) {
      state.firstLoad = true
    }
    state.isLoading = true

    await getPortalOsobaApi()
      .searchOsobaPortalOsoba(
        searchValues.meno,
        searchValues.priezvisko,
        searchValues.rodneCislo,
        searchValues.datumNarodenia
      )
      .then((r) => {
        state.searchResults = r.data
        userSession.setSearchValues(searchValues)
        state.isLoading = false
      })
      .catch(console.error)
  } else {
    state.firstLoad = false
    state.showFillError = true
  }
}

const {
  remove: removePartnerSubjektu,
  fields: fieldsPartnerSubjektu,
  update: updatePartnerSubjektu,
  push: pushPartnerSubjektu,
} = useFieldArray<ZoPPMData>('projekt.partneri')

const {
  remove: removePartnerSubjektuOsobaRef,
  fields: fieldsPartnerSubjektuOsobaRef,
  update: updatePartnerSubjektuOsobaRef,
  push: pushPartnerSubjektuOsobaRef,
} = useFieldArray<ZoPPMData>('projekt.partneri.partnerSubjektu.osobaRef')

const { remove, fields, update, push } =
  useFieldArray<ZoppmPartnerSubjektu>('partnerSubjektu')

const odobratPartneraAktualizovanyOsobaPo = async () => {
  let indexAktualizovana = -1
  for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
    if (formValues.value.projekt.partneri[i].partnerSubjektu['organizacia']) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['organizacia'].ico ===
        formValues?.value?.organizaciaForm[0].ico
      ) {
        indexAktualizovana = i
      }
    }
  }

  let index: undefined
  let ico = formValues?.value?.organizaciaForm[0].ico
  index = partneriData.value.findIndex((item) =>
    item.menoUplne_datumNarodenia.includes(ico)
  )

  removePartnerSubjektu(indexAktualizovana)
  // removeOrganizacia(schemeIndexOs)
  partneriData.value.splice(index, 1)

  removeOrganizaciaForm(0)
  formCleaner()
  isSectionAddPartnerVisible.value = false
  PoRadioChecked.value = false
  isSectionVisible.value = false
  isPOVisible.value = false
}

const odobratPartneraAktualizovanyOsoba = async () => {
  let indexAktualizovana = -1
  for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
    if (formValues.value.projekt.partneri[i].partnerSubjektu['osoba']) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].meno ===
          formValues?.value?.osobaForm[0].meno &&
        formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].priezvisko ===
          formValues?.value?.osobaForm[0].priezvisko
      ) {
        indexAktualizovana = i
      }
    }
  }

  let index: undefined
  let meno = formValues?.value?.osobaForm[0].meno
  let priezvisko = formValues?.value?.osobaForm[0].priezvisko
  let ulica = formValues?.value?.osobaForm[0].primarnaAdresa.ulica
  let obec = formValues?.value?.osobaForm[0].primarnaAdresa.obec
  index = partneriData.value.findIndex(
    (item) =>
      item.menoUplne_datumNarodenia.includes(meno) &&
      item.menoUplne_datumNarodenia.includes(priezvisko)
  )
  removePartnerSubjektu(indexAktualizovana)
  // removeOsoba(schemeIndexOs)
  partneriData.value.splice(index, 1)

  removeOsobaForm(0)
  formCleaner()
  isSectionAddPartnerVisible.value = false
  FoRadioChecked.value = false
  isSectionVisible.value = false
  isFOVisible.value = false
}

const odobratPartneraOsoba = async (osoba: OsobaRef) => {
  let index: undefined
  index = partneriData.value.findIndex(
    (item) =>
      item.menoUplne_datumNarodenia.includes(osoba.menoUplne) &&
      (item.adresaUplna.includes(osoba.adresaUplna) || osoba.adresaUplna == undefined)
  )

  if (index != -1) {
    partneriData.value.splice(index, 1)

    let indexOdobrat = -1
    for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
      if (formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef']) {
        if (
          formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef'].menoUplne ===
          osoba.menoUplne
        ) {
          indexOdobrat = i
        }
      }
    }
    removePartnerSubjektu(indexOdobrat)
  }

  FoRadioChecked.value = false
  isSectionVisible.value = false
  isFOVisible.value = false
  formCleaner()
}

function formCleaner() {
  state.firstLoad = false
  state.searchResults = []
  menoForm.value = ''
  state.showFillError = false
  statePo.showFillErrorPartner = false
  priezviskoForm.value = ''

  datumNarodeniaForm.value = undefined
  rodneCisloForm.value = ''
  subjektDic.value = ''
  subjektIco.value = ''
  aktualizovatPartnera.value = false
  odstranitPartnera.value = false
  vytvoritNovehoPartnera.value = true
}
const odobratPartneraOrganizacia = async (organizacia: OrganizaciaRef) => {
  let index: undefined
  index = partneriData.value.findIndex(
    (item) =>
      item.menoUplne_datumNarodenia.includes(organizacia.nazov) &&
      (item.adresaUplna.includes(organizacia.adresaUplna) ||
        organizacia.adresaUplna == null ||
        organizacia.adresaUplna == '')
  )

  if (index != -1) {
    partneriData.value.splice(index, 1)

    let indexOdobrat = -1
    for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
      if (formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef']) {
        if (
          formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef'].ico ===
          organizacia.ico
        ) {
          indexOdobrat = i
        }
      }
    }
    removePartnerSubjektu(indexOdobrat)
  }

  PoRadioChecked.value = false
  isSectionVisible.value = false
  isPOVisible.value = false
  subjektIco.value = ''
  subjektDic.value = ''
}

const {
  remove: removeOsoba,
  fields: fieldsOsoba,
  update: updateOsoba,
  push: pushOsoba,
} = useFieldArray<ZoPPMData>('partnerSubjektu.osoba')
const {
  remove: removeOrganizacia,
  fields: fieldsOrganizacia,
  update: updateOrganizacia,
  push: pushOrganizacia,
} = useFieldArray<OrganizaciaCreate>('partnerSubjektu.organizacia')
const formValues = useFormValues()

const aktualizovatPartneraPo = async () => {
  let indexAktualizovana = -1
  for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
    if (formValues.value.projekt.partneri[i].partnerSubjektu['organizacia']) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['organizacia'].ico ===
        formValues?.value?.organizaciaForm[0].ico
      ) {
        indexAktualizovana = i
      }
    }
  }
  let adresa = ''

  adresa +=
    formValues?.value?.organizaciaForm[0]?.sidlo?.ulica != undefined
      ? formValues?.value?.organizaciaForm[0].sidlo.ulica
      : ''

  adresa +=
    formValues?.value?.organizaciaForm[0]?.sidlo?.supisneCislo != undefined
      ? ' ' + formValues?.value?.organizaciaForm[0].sidlo.supisneCislo
      : ''

  adresa +=
    formValues?.value?.organizaciaForm[0]?.sidlo?.psc != undefined
      ? ', ' + formValues?.value?.organizaciaForm[0].sidlo.psc
      : ''

  if (formValues?.value?.organizaciaForm[0]?.sidlo?.obecZahranicie != undefined) {
    adresa += ' ' + formValues?.value?.organizaciaForm[0].sidlo.obecZahranicie
  } else {
    adresa +=
      formValues?.value?.organizaciaForm[0]?.sidlo?.obec != undefined
        ? ' ' + formValues?.value?.organizaciaForm[0].sidlo.obec.nazov
        : ''
  }

  let newOsoba = {
    menoUplne_datumNarodenia:
      formValues?.value?.organizaciaForm[0].nazov +
      ' (IČO: ' +
      formValues?.value?.organizaciaForm[0].ico +
      ')',
    adresaUplna: adresa,
  }

  if (
    formValues?.value?.organizaciaForm[0].ico.length > 0 &&
    formValues?.value?.organizaciaForm[0].nazov.length > 0 &&
    formValues?.value?.organizaciaForm[0].pravnaForma != ''
  ) {
    let index: undefined
    let ico = formValues?.value?.organizaciaForm[0].ico
    index = partneriData.value.findIndex((item) =>
      item.menoUplne_datumNarodenia.includes(ico)
    )

    removePartnerSubjektu(indexAktualizovana)
    // removeOrganizacia(schemeIndexOs)
    partneriData.value.splice(index, 1)
    partneriData.value.push(newOsoba)

    pushPartnerSubjektu({
      partnerSubjektu: { organizacia: formValues?.value?.organizaciaForm[0] },
    })

    // pushOrganizacia(formValues?.value?.partnerSubjektu?.organizaciaForm[0])
    removeOrganizaciaForm(0)
    formCleaner()
    isSectionAddPartnerVisible.value = false
    PoRadioChecked.value = false
    isSectionVisible.value = false
    isPOVisible.value = false
  } else {
    statePo.showFillErrorPartner = true
  }
}

const aktualizovatPartneraFo = async () => {
  let indexAktualizovana = -1
  for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
    if (formValues.value.projekt.partneri[i].partnerSubjektu['osoba']) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].meno ===
          formValues?.value?.osobaForm[0].meno &&
        formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].priezvisko ===
          formValues?.value?.osobaForm[0].priezvisko
      ) {
        indexAktualizovana = i
      }
    }
  }

  let adresa = ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.ulica != undefined
      ? formValues?.value?.osobaForm[0].primarnaAdresa.ulica
      : ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.supisneCislo != undefined
      ? ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.supisneCislo
      : ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.psc != undefined
      ? ', ' + formValues?.value?.osobaForm[0].primarnaAdresa.psc
      : ''

  if (formValues?.value?.osobaForm[0].primarnaAdresa.obecZahranicie != undefined) {
    adresa += ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.obecZahranicie
  } else {
    adresa +=
      formValues?.value?.osobaForm[0].primarnaAdresa.obec != undefined
        ? ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.obec.nazov
        : ''
  }

  let newOsoba = {
    menoUplne_datumNarodenia:
      formValues?.value?.osobaForm[0].meno +
      ' ' +
      formValues?.value?.osobaForm[0].priezvisko +
      ' (nar. ' +
      new Date(formValues?.value?.osobaForm[0].datumNarodenia).getFullYear() +
      ')',
    adresaUplna: adresa,
  }

  let index: undefined
  let meno = formValues?.value?.osobaForm[0].meno
  let priezvisko = formValues?.value?.osobaForm[0].priezvisko
  let ulica = formValues?.value?.osobaForm[0].primarnaAdresa.ulica
  let obec = formValues?.value?.osobaForm[0].primarnaAdresa.obec
  index = partneriData.value.findIndex(
    (item) =>
      item.menoUplne_datumNarodenia.includes(meno) &&
      item.menoUplne_datumNarodenia.includes(priezvisko)
  )

  removePartnerSubjektu(indexAktualizovana)
  //removeOsoba(schemeIndexOs)
  partneriData.value.splice(index, 1)
  partneriData.value.push(newOsoba)

  pushPartnerSubjektu({ partnerSubjektu: { osoba: formValues?.value?.osobaForm[0] } })

  // pushOsoba(formValues?.value?.osobaForm[0])
  removeOsobaForm(0)
  formCleaner()
  isSectionAddPartnerVisible.value = false
  FoRadioChecked.value = false
  isSectionVisible.value = false
  isFOVisible.value = false
}

const createNewPartnerPo = async () => {
  if (
    formValues?.value?.organizaciaForm[0].ico != undefined &&
    formValues?.value?.organizaciaForm[0].nazov != undefined &&
    formValues?.value?.organizaciaForm[0].pravnaForma != ''
  ) {
    pushPartnerSubjektu({
      partnerSubjektu: { organizacia: formValues?.value?.organizaciaForm[0] },
    })

    let adresa = ''

    adresa +=
      formValues?.value?.organizaciaForm[0]?.sidlo?.ulica != undefined
        ? formValues?.value?.organizaciaForm[0].sidlo.ulica
        : ''

    adresa +=
      formValues?.value?.organizaciaForm[0]?.sidlo?.supisneCislo != undefined
        ? ' ' + formValues?.value?.organizaciaForm[0].sidlo.supisneCislo
        : ''

    adresa +=
      formValues?.value?.organizaciaForm[0]?.sidlo?.psc != undefined
        ? ', ' + formValues?.value?.organizaciaForm[0].sidlo.psc
        : ''

    if (formValues?.value?.organizaciaForm[0]?.sidlo?.obecZahranicie != undefined) {
      adresa += ' ' + formValues?.value?.organizaciaForm[0].sidlo.obecZahranicie
    } else {
      adresa +=
        formValues?.value?.organizaciaForm[0]?.sidlo?.obec != undefined
          ? ' ' + formValues?.value?.organizaciaForm[0].sidlo.obec.nazov
          : ''
    }

    let newOsoba = {
      menoUplne_datumNarodenia:
        formValues?.value?.organizaciaForm[0].nazov +
        ' (IČO: ' +
        formValues?.value?.organizaciaForm[0].ico +
        ')',
      adresaUplna: adresa,
    }
    partneriData.value.push(newOsoba)
    formCleaner()
    isSectionAddPartnerVisible.value = false
    PoRadioChecked.value = false
    isSectionVisible.value = false
    isPOVisible.value = false
    statePo.showFillErrorPartner = false
  } else {
    statePo.showFillErrorPartner = true
  }
}
const createNewPartner = async () => {
  pushPartnerSubjektu({ partnerSubjektu: { osoba: formValues?.value?.osobaForm[0] } })

  let adresa = ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.ulica != undefined
      ? formValues?.value?.osobaForm[0].primarnaAdresa.ulica
      : ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.supisneCislo != undefined
      ? ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.supisneCislo
      : ''

  adresa +=
    formValues?.value?.osobaForm[0].primarnaAdresa.psc != undefined
      ? ', ' + formValues?.value?.osobaForm[0].primarnaAdresa.psc
      : ''

  if (formValues?.value?.osobaForm[0].primarnaAdresa.obecZahranicie != undefined) {
    adresa += ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.obecZahranicie
  } else {
    adresa +=
      formValues?.value?.osobaForm[0].primarnaAdresa.obec != undefined
        ? ' ' + formValues?.value?.osobaForm[0].primarnaAdresa.obec.nazov
        : ''
  }
  let newOsoba = {
    menoUplne_datumNarodenia:
      formValues?.value?.osobaForm[0].meno +
      ' ' +
      formValues?.value?.osobaForm[0].priezvisko +
      ' (nar. ' +
      new Date(formValues?.value?.osobaForm[0].datumNarodenia).getFullYear() +
      ')',
    adresaUplna: adresa,
  }

  partneriData.value.push(newOsoba)
  showDatumNarodenia.value = undefined
  rodneCisloForm.value = ''
  formCleaner()
  isSectionAddPartnerVisible.value = false
  FoRadioChecked.value = false
  isSectionVisible.value = false
  isFOVisible.value = false
}

const handleAddOsoba = async (osoba: OsobaRef) => {
  let schemaIndex = -1
  if (formValues.value.projekt.partneri !== undefined) {
    for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef'] !== undefined
      ) {
        if (
          formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef'].osobaId ===
          osoba.osobaId
        ) {
          schemaIndex = i
        }
      }
    }
  }

  if (schemaIndex == -1) {
    let newOsoba = {
      menoUplne_datumNarodenia:
        osoba.menoUplne + ' (nar. ' + new Date(osoba.datumNarodenia).getFullYear() + ')',
      adresaUplna: osoba.adresaUplna,
    }
    partneriData.value.push(newOsoba)

    pushPartnerSubjektu({ partnerSubjektu: { osobaRef: osoba } })
  }

  FoRadioChecked.value = false
  isSectionVisible.value = false
  isFOVisible.value = false
  isSectionShowDetailPartnerVisible.value = false
  formCleaner()
}
const detailOsoba = ref<OsobaCreate[]>([])
const detailOrganizacia = ref<OrganizaciaCreate[]>([])
const {
  remove: removeOsobaForm,
  fields: fieldsOsobaForm,
  update: updateOsobaForm,
  push: pushOsobaForm,
} = useFieldArray<OsobaCreate>('osobaForm')

const {
  remove: removeOrganizaciaForm,
  fields: fieldsOrganizaciaForm,
  update: updateOrganizaciaForm,
  push: pushOrganizaciaForm,
} = useFieldArray<OrganizaciaCreate>('organizaciaForm')

const detailOsobaAdresa = ref('')
const toggleShowDetail = async (value) => {
  if (isSectionAddPartnerVisible.value == false) {
    if (value.includes('IČO')) {
      let menoDatumNar = value.split('(')
      menoDatumNar[1] = menoDatumNar[1].slice(0, -1)
      menoDatumNar[1] = menoDatumNar[1].slice(5)
      detailOrganizacia.value = []

      let index = -1
      for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
        if (formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef']) {
          if (
            formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef'].ico ===
            menoDatumNar[1]
          ) {
            index = i
          }
        }
      }

      if (index == -1) {
        let indexAktualizovana = -1
        for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
          if (formValues.value.projekt.partneri[i].partnerSubjektu['organizacia']) {
            if (
              formValues.value.projekt.partneri[i].partnerSubjektu['organizacia'].ico ===
              menoDatumNar[1]
            ) {
              indexAktualizovana = i
            }
          }
        }

        removeOrganizaciaForm(0)
        pushOrganizaciaForm(
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu
            .organizacia
        )
        krajSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.organizacia.sidlo.kraj
        okresSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.organizacia.sidlo.okres
        obecSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.organizacia.sidlo.obec
        ulicaSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.organizacia.sidlo.ulica
        //   pushOrganizaciaForm(formValues?.value?.partnerSubjektu?.organizacia[schemeIndexOs])
        isSectionAddPartnerVisible.value = true
        isSectionShowDetailPartnerVisible.value = false
        aktualizovatPartnera.value = true
        odstranitPartnera.value = true
        vytvoritNovehoPartnera.value = false
      } else {
        let responseForId =
          await organizaciaPublicApi.portalSearchOrganizaciaOrganizaciaPublic(
            menoDatumNar[1]
          )

        let response = await getOrganizacieApi().portalGetOrganizaciaOrganizacie(
          responseForId.data[0].organizaciaId
        )

        detailOrganizacia.value = response.data

        PoRadioChecked.value = true
        isFOVisible.value = false
        isPOVisible.value = true
        isSectionShowDetailPartnerVisiblePo.value = true
        isSectionShowDetailPartnerVisible.value = false

        isSectionAddPartnerVisible.value = false
        formCleaner()
      }
      PoRadioChecked.value = true
      isFOVisible.value = false
      isPOVisible.value = true
    } else {
      let menoDatumNar = value.split('(')
      menoDatumNar[0] = menoDatumNar[0].slice(0, -1)
      menoDatumNar[1] = menoDatumNar[1].slice(0, -1)
      menoDatumNar[1] = menoDatumNar[1].slice(5)
      let menoDatumNarSep = menoDatumNar[0].split(' ')
      detailOsoba.value = []

      let index = -1
      for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
        if (
          formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef'] !== undefined
        ) {
          if (
            formValues.value.projekt.partneri[i].partnerSubjektu['osobaRef'].menoUplne ===
            menoDatumNar[0]
          ) {
            index = i
          }
        }
      }

      if (index == -1) {
        let menoPriezvisko = menoDatumNar[0].split(' ')

        let indexAktualizovana = -1
        for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
          if (formValues.value.projekt.partneri[i].partnerSubjektu['osoba']) {
            if (
              value.includes(
                formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].meno
              ) &&
              value.includes(
                formValues.value.projekt.partneri[i].partnerSubjektu['osoba'].priezvisko
              )
            ) {
              indexAktualizovana = i
              break
            }
          }
        }

        removeOsobaForm(0)

        statnaPrislusnostSelectValues.id =
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu[
            'osoba'
          ].statnaPrislusnost.id
        statnaPrislusnostSelectValues.kodZdroj =
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu[
            'osoba'
          ].statnaPrislusnost.kodZdroj
        statnaPrislusnostSelectValues.nazov =
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu[
            'osoba'
          ].statnaPrislusnost.nazov

        if (
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu.osoba
            .primarnaAdresa.stat.nazov == 'Slovenská republika'
        ) {
          skPredefined.value = true
        } else {
          skPredefined.value = false
        }

        datumNarodeniaForm.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.datumNarodenia
        if (datumNarodeniaForm.value != undefined) {
          open.value = true
          modeDate.value = 'date'
        }
        krajSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.primarnaAdresa.kraj
        okresSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.primarnaAdresa.okres
        obecSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.primarnaAdresa.obec
        ulicaSelection.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.primarnaAdresa.ulica

        statnaPrislusnost.value = statnaPrislusnostSelectValues
        titulPredMenomArrayForm.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.titulyPredMenom
        titulZaMenomArrayForm.value =
          formValues.value.projekt.partneri[
            indexAktualizovana
          ].partnerSubjektu.osoba.titulyZaMenom
        pushOsobaForm(
          formValues.value.projekt.partneri[indexAktualizovana].partnerSubjektu.osoba
        )
        isSectionShowDetailPartnerVisible.value = false
        isSectionAddPartnerVisible.value = true
        aktualizovatPartnera.value = true
        odstranitPartnera.value = true
        vytvoritNovehoPartnera.value = false
      } else {
        const response = await getOsobyApi().portalGetOsobaOsoby(
          formValues.value.projekt.partneri[index].partnerSubjektu.osobaRef.osobaId
        )
        /* const response = await getPortalOsobaApi().searchOsobaPortalOsoba(
        menoDatumNarSep[0],
        menoDatumNarSep[1],
        undefined,
        menoDatumNar[1]
      )*/

        response.data.datumNarodenia = format(response.data.datumNarodenia)
        detailOsoba.value = response.data
        detailOsobaAdresa.value = ''
        detailOsobaAdresa.value =
          formValues.value.projekt.partneri[index].partnerSubjektu.osobaRef.adresaUplna
        isSectionShowDetailPartnerVisible.value = true
        isSectionShowDetailPartnerVisiblePo.value = false
        isSectionAddPartnerVisible.value = false
        formCleaner()
      }
      FoRadioChecked.value = true
      isFOVisible.value = true
      isPOVisible.value = false
    }

    isSectionVisible.value = true
    isSectionSearchPartnerVisible.value = false
  }
}

function format(inputDate) {
  let date = new Date(inputDate)
  if (!isNaN(date.getTime())) {
    return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear()
  }
}

const handleOpen = (isOpen: boolean) => {
  setShowDatumNarodenia(isOpen)
  if (isOpen) {
    rodneCisloForm.value = ''
  } else {
    datumNarodeniaForm.value = undefined
  }
}
const handleCreate = async () => {
  modeDate.value = ''
  statnaPrislusnost.value = skSelectOption
  titulPredMenomArrayForm.value = []
  titulZaMenomArrayForm.value = []
  skPredefined.value = true
  krajSelection.value = undefined
  okresSelection.value = undefined
  obecSelection.value = undefined
  ulicaSelection.value = ''
  isSectionAddPartnerVisible.value = false
  toggleNewPartnerSection()
  if (datumNarodeniaForm.value == undefined) {
    open.value = false
  } else {
    open.value = true
  }
}

const foundSubjekty = ref<OrganizaciaRef[]>([])

let searchValuesPo = {
  subjektIco: '',
  subjektDic: '',
}

const organizaciaPublicApi = new apisPublic.OrganizaciaPublicApi()
const subjektIco = ref('')
const subjektDic = ref('')

const searchSubjekty = async () => {
  if (!state.firstLoad) {
    state.firstLoad = true
  }
  searchValuesPo.subjektIco = subjektIco.value
  searchValuesPo.subjektDic = subjektDic.value
  state.isLoading = true
  foundSubjekty.value = []

  if (subjektIco.value == '' && subjektDic.value == '') {
    state.showFillError = true
    state.firstLoad = false
  } else {
    state.showFillError = false
  }
  if (subjektIco.value != '') {
    let response = await organizaciaPublicApi.portalSearchOrganizaciaOrganizaciaPublic(
      subjektIco.value
    )
    foundSubjekty.value = response.data
    if (subjektDic.value != '' && foundSubjekty.value.length > 0) {
      let response = await organizaciaPublicApi.portalSearchOrganizaciaOrganizaciaPublic(
        subjektDic.value
      )
      foundSubjekty.value = response.data
    }
  }
  if (
    subjektIco.value == '' &&
    subjektDic.value != '' &&
    foundSubjekty.value.length == 0
  ) {
    const response = await organizaciaPublicApi.portalSearchOrganizaciaOrganizaciaPublic(
      subjektDic.value
    )
    foundSubjekty.value = response.data
  }
  state.isLoading = false
  return foundSubjekty
}

const replaceResultsTextPo = computed(() => {
  const l = foundSubjekty.value.length
  const replace = { pocet: l }
  if (l === 0 || l > 4) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdenych'),
      evidovane: t('subjektOsoby.search.evidovanych'),
      organizacie: t('subjektOsoby.search.organizacii'),
    }
  } else if (l > 1 && l < 4) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdene'),
      evidovane: t('subjektOsoby.search.evidovane'),
      organizacie: t('subjektOsoby.search.organizacie'),
    }
  } else if (l === 1) {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdena'),
      evidovane: t('subjektOsoby.search.evidovana'),
      organizacie: t('subjektOsoby.search.organizacie'),
    }
  } else {
    return {
      ...replace,
      najdene: t('subjektOsoby.search.najdene'),
      evidovane: t('subjektOsoby.add.evidovane'),
      organizacie: t('subjektOsoby.add.organizacie'),
    }
  }
})

const {
  remove: removeOrg,
  fields: fieldsOrg,
  update: updateOrg,
  push: pushOrg,
} = useFieldArray<OrganizaciaRef>('partnerSubjektu.organizaciaRef')

const handleAddOrganizacia = async (organizacia: OrganizaciaRef) => {
  let schemaIndex = -1
  if (formValues.value.projekt.partneri !== undefined) {
    for (let i = 0; i < formValues.value.projekt.partneri.length; i++) {
      if (
        formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef'] !==
        undefined
      ) {
        if (
          formValues.value.projekt.partneri[i].partnerSubjektu['organizaciaRef']
            .organizaciaId === organizacia.organizaciaId
        ) {
          schemaIndex = i
        }
      }
    }
  }

  if (schemaIndex == -1) {
    let newOsoba = {
      menoUplne_datumNarodenia: organizacia.nazov + ' (IČO: ' + organizacia.ico + ')',
      adresaUplna: organizacia.adresaUplna,
    }
    partneriData.value.push(newOsoba)
    pushPartnerSubjektu({ partnerSubjektu: { organizaciaRef: organizacia } })
  }
  state.searchResults = []
  state.firstLoad = false
  PoRadioChecked.value = false
  isSectionVisible.value = false
  isPOVisible.value = false
  formCleaner()
  isSectionShowDetailPartnerVisible.value = false
  subjektIco.value = ''
  subjektDic.value = ''
}

const toggleNewPartnerSection = () => {
  if (isSectionAddPartnerVisible.value == true) {
    isSectionAddPartnerVisible.value = false
    isSectionSearchPartnerVisible.value = true
  } else {
    isSectionAddPartnerVisible.value = true
    isSectionSearchPartnerVisible.value = false
  }
}

watchEffect(async () => {
  partneriData.value = []
  partneriDataTable.value = []
  if (formValues.value.projekt?.partneri !== undefined) {
    for (let k = 0; k < formValues.value.projekt.partneri.length; k++) {
      if (formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']) {
        let adresa = ''

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.ulica != undefined
            ? formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                .primarnaAdresa.ulica + ' '
            : ''

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.orientacneCislo != ''
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                .primarnaAdresa.orientacneCislo
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.orientacneCislo != '' &&
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.supisneCislo != ''
        ) {
          adresa += '/'
        }

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.supisneCislo != ''
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                .primarnaAdresa.supisneCislo +
              ', '
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.orientacneCislo != '' &&
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.supisneCislo == ''
        ) {
          adresa += ', '
        }

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.psc != undefined
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                .primarnaAdresa.psc +
              ', '
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.obecZahranicie != undefined
        ) {
          adresa +=
            '' +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
              ?.primarnaAdresa.obecZahranicie +
            ', '
        } else {
          adresa +=
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
              ?.primarnaAdresa?.obec != undefined
              ? '' +
                formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                  .primarnaAdresa.obec.nazov +
                ', '
              : ''
        }
        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']?.primarnaAdresa
            ?.stat != undefined
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
                .primarnaAdresa.stat.nazov
            : ''
        let datumNarRodne =
          ' (nar. ' +
          new Date(
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu[
              'osoba'
            ]?.datumNarodenia
          ).getFullYear() +
          ')'

        let titulyPredMeno = ''
        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
            .titulyPredMenom != null
        ) {
          var titulyPredMenoArray = Object.keys(
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
              .titulyPredMenom
          ).map(function (key) {
            return formValues
              ?.value?.projekt.partneri[k]?.partnerSubjektu['osoba'].titulyPredMenom[key].nazov
          })
          titulyPredMeno = titulyPredMenoArray.join(' ') + ' '
        }

        let titulyZaMeno = ''
        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba']
            .titulyZaMenom != null
        ) {
          var titulyZaMenoArray = Object.keys(
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba'].titulyZaMenom
          ).map(function (key) {
            return formValues
              ?.value?.projekt.partneri[k]?.partnerSubjektu['osoba'].titulyZaMenom[key].nazov
          })
          titulyZaMeno = ' ' + titulyZaMenoArray.join(' ')
        }

        let newOsoba = {
          menoUplne_datumNarodenia:
            titulyPredMeno +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba'].meno +
            ' ' +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osoba'].priezvisko +
            titulyZaMeno +
            ' ' +
            datumNarRodne,
          adresaUplna: adresa,
        }

        partneriData.value.push(newOsoba)
      }

      if (formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osobaRef']) {
        let newOsoba = {
          menoUplne_datumNarodenia:
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osobaRef']
              .menoUplne +
            ' (nar. ' +
            new Date(
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu[
                'osobaRef'
              ].datumNarodenia
            ).getFullYear() +
            ')',
          adresaUplna:
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['osobaRef']
              .adresaUplna,
        }

        partneriData.value.push(newOsoba)
      }

      if (formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']) {
        let adresa = ''

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.ulica != undefined
            ? formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
                .ulica + ' '
            : ''

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.orientacneCislo != ''
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
                .orientacneCislo
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.orientacneCislo != '' &&
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.supisneCislo != ''
        ) {
          adresa += '/'
        }

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.supisneCislo != ''
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
                .supisneCislo +
              ', '
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.orientacneCislo != '' &&
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.supisneCislo == ''
        ) {
          adresa += ', '
        }

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.psc != undefined
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
                .psc +
              ', '
            : ''

        if (
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.okresZahranicie != undefined
        ) {
          adresa +=
            '' +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
              .okresZahranicie +
            ', '
        } else {
          adresa +=
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
              ?.obec != undefined
              ? '' +
                formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']
                  .sidlo.obec.nazov +
                ', '
              : ''
        }

        adresa +=
          formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia']?.sidlo
            ?.stat != undefined
            ? '' +
              formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].sidlo
                .stat.nazov
            : ''

        let newOsoba = {
          menoUplne_datumNarodenia:
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].nazov +
            ' (IČO: ' +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizacia'].ico +
            ')',
          adresaUplna: adresa,
        }

        partneriData.value.push(newOsoba)
      }

      if (formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizaciaRef']) {
        let newOsoba = {
          menoUplne_datumNarodenia:
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizaciaRef']
              .nazov +
            ' (IČO: ' +
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizaciaRef']
              .ico +
            ')',
          adresaUplna:
            formValues?.value?.projekt.partneri[k]?.partnerSubjektu['organizaciaRef']
              .adresaUplna,
        }

        partneriData.value.push(newOsoba)
      }
    }
  }
})
</script>

<template>
  <IDSKContainer>
    <div class="idsk-crossroad-line"></div>
    <section id="zoznam-partnerov">
      <IDSKTable
        v-if="partneriData?.length > 0"
        :id="`zoznam-partnerov-tabulka`"
        :header-off="true"
        :columns="{
          menoUplne_datumNarodenia: '',
          adresaUplna: '',
        }"
        :data="partneriData"
      >
        <template #cell(menoUplne_datumNarodenia)="{ value }">
          <a
            class="govuk-heading-m heading--blue govuk-link govuk-link--no-underline"
            href="#"
            @click="toggleShowDetail(value)"
          >
            {{ value }}
          </a>
        </template>
        <template #cell(adresaUplna)="{ value }">
          <a class="heading--blue">
            {{ value }}
          </a>
        </template>
      </IDSKTable>
      <table v-else class="govuk-table">
        <tbody class="govuk-table__body">
          <tr class="govuk-table__row">
            <td class="govuk-table__cell">
              <div class="govuk-heading-m heading--blue">
                {{ t('zoppm.step18.nieJePartner') }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section id="pridanie-partnerov">
      <IDSKButtonGroup class="mb-0">
        <IDSKButton
          :data-test-id="`zoppm-partneri-po-pridat-partnera`"
          @click="toggleSection"
        >
          {{ t('zoppm.step18.pridatPartneraProjektu') }}
        </IDSKButton>

        <IDSKButton
          v-if="partneriData?.length === 0"
          variant="secondary"
          type="submit"
          :disabled="showErrors"
          :data-test-id="`zoppm-partneri-po-pokracovat-bez-partnerov`"
          @click.prevent="() => setPrevNextStep(1, { ref: undefined })"
        >
          {{ t('zoppm.step18.pokracovatBezPartnerov') }}
        </IDSKButton>
      </IDSKButtonGroup>
    </section>
    <section v-if="isSectionVisible" id="definovaniePartnera">
      <fieldset class="govuk-fieldset">
        <div class="govuk-form-group">
          <div class="govuk-radios govuk-radios--conditional" data-module="govuk-radios">
            <div class="govuk-radios__item">
              <input
                id="FoRadio"
                class="govuk-radios__input"
                name="partneriRadio"
                type="radio"
                value="FO"
                aria-expanded="false"
                :checked="FoRadioChecked"
                @change="toggleSectionRadioFO"
              />
              <label class="govuk-label govuk-radios__label">
                {{ t('zoppm.step18.fyzickaOsoba') }}
              </label>
            </div>
            <div v-if="isFOVisible" class="govuk-radios__conditional">
              <template v-if="isSectionSearchPartnerVisible">
                <IDSKContainer>
                  <IDSKErrorSummary
                    v-if="state.showFillError"
                    :title="t('globalne.chyba')"
                    :description="t('zoppm.step18.chybaDate')"
                    class="mb-6"
                  />
                  <section>
                    <ValidationWrapper v-slot="slotProps" name="osobaForm[0].meno">
                      <IDSKInput
                        id="osobaForm.meno"
                        v-bind="slotProps"
                        v-model="menoForm"
                        :label="t('globalne.meno')"
                      />
                    </ValidationWrapper>
                    <ValidationWrapper v-slot="slotProps" name="osobaForm[0].priezvisko">
                      <IDSKInput
                        id="osobaForm.priezvisko"
                        v-bind="slotProps"
                        v-model="priezviskoForm"
                        :label="t('globalne.priezvisko')"
                      />
                    </ValidationWrapper>

                    <ValidationWrapper v-slot="slotProps" name="osobaForm[0].rodneCislo">
                      <IDSKInput
                        id="osobaForm.rodneCislo"
                        v-bind="slotProps"
                        v-model="rodneCisloForm"
                        hint="Zadáva sa bez lomítka"
                        :label="t('globalne.rodneCislo')"
                        :hint="t('globalne.rodneCisloHint')"
                        class="govuk-input--width-10 mb-5"
                        :disabled="showDatumNarodenia || undefined"
                      />
                    </ValidationWrapper>
                    <IDSKParagraph class="govuk-body-s">
                      {{ t('subjektOsoby.search.dateHint') }}
                    </IDSKParagraph>
                    <IDSKDetails
                      :title="t('subjektOsoby.search.dateSummaryTitle')"
                      class="mb-8"
                      @is-open="handleOpen"
                    >
                      <IDSKFieldset>
                        <template #legend>{{
                          t('subjektOsoby.search.dateLegend')
                        }}</template>
                        <template #fields>
                          <ValidationWrapper
                            v-slot="slotProps"
                            name="osobaForm[0].datumNarodenia"
                          >
                            <IDSKDateInputSeparated
                              id="osoba.datumNarodenia"
                              v-model="datumNarodeniaForm"
                              v-bind="slotProps"
                              :model-value="undefined"
                            />
                          </ValidationWrapper>
                        </template>
                      </IDSKFieldset>
                    </IDSKDetails>
                    <IDSKButtonGroup>
                      <IDSKButton
                        variant="secondary"
                        element="input"
                        :value="t('zoppm.step18.vyhladatPartneraProjektu')"
                        :data-test-id="`zoppm-partneri-po-vyhladat-partnera`"
                        @click="onSubmit()"
                      />
                    </IDSKButtonGroup>
                  </section>

                  <section v-if="state.isLoading">
                    <IDSKParagraph>Načítavanie...</IDSKParagraph>
                  </section>
                  <section v-else-if="state.firstLoad && state.searchResults">
                    <IDSKParagraph class="mb-3">
                      {{ t('subjektOsoby.search.searchTextStart') }}
                      <span v-if="searchValues.meno || searchValues.priezvisko">
                        <strong>
                          {{
                            [searchValues.meno, searchValues.priezvisko]
                              .filter((v) => !!v)
                              .join(' ')
                          }}
                        </strong>
                        <span
                          v-if="searchValues.rodneCislo || searchValues.datumNarodenia"
                          >,
                        </span>
                      </span>
                      <span v-if="searchValues.rodneCislo">
                        {{ t('subjektOsoby.search.searchTextBirthNumber') }}
                        <strong>{{ searchValues.rodneCislo }}</strong>
                        <span v-if="searchValues.datumNarodenia">, </span>
                      </span>
                      <span v-if="searchValues.datumNarodenia">
                        {{ t('subjektOsoby.search.searchTextDate') }}
                        <strong>{{ datumNarodeniaFormatted }}</strong></span
                      >:
                      <br />
                      {{ t('subjektOsoby.search.searchSummary', replaceResultsText) }}
                    </IDSKParagraph>
                    <IDSKDivider class="mb-6" />
                    <template v-if="searchCountIsEmpty">
                      <IDSKErrorSummary
                        :title="t('subjektOsoby.search.errorEmptySearch.title')"
                      >
                        <template #description>
                          {{ t('subjektOsoby.search.errorEmptySearch.descriptionError')
                          }}<br /><br />
                          {{ t('subjektOsoby.search.errorEmptySearch.descriptionOptions')
                          }}<br /><br />
                          {{ t('zoppm.step18.descErrorPartneri') }}
                        </template>
                      </IDSKErrorSummary>
                      <IDSKButton
                        :data-test-id="`zoppm-partneri-po-vytvorit-partnera`"
                        @click="handleCreate"
                      >
                        {{ t('zoppm.step18.vytvoritPartneraProjektu') }}
                      </IDSKButton>
                    </template>
                    <dl>
                      <template v-for="(osoba, id) in state.searchResults" :key="id">
                        <dt>
                          <IDSKParagraph variant="s" class="mb-0">
                            {{ osoba?.menoUplne }}
                          </IDSKParagraph>
                        </dt>
                        <dd class="ml-0">
                          <span class="govuk-hint">
                            {{ osoba?.adresaUplna }}
                          </span>
                          <br />
                          <IDSKButton
                            :data-test-id="`zoppm-partneri-po-vybrat-partnera-${id}`"
                            @click="() => handleAddOsoba(osoba)"
                          >
                            {{ t('zoppm.step18.vybratPartneraProjektu') }}
                          </IDSKButton>
                        </dd>
                      </template>
                      <IDSKWarningTextCustom
                        v-if="!searchCountIsEmpty"
                        variant="warningInfo"
                        class="mb-4 pr-2"
                      >
                        {{ t('zoppm.step18.formularJeUkazkovy') }}
                      </IDSKWarningTextCustom>
                      <IDSKButton
                        v-if="!searchCountIsEmpty"
                        class="govuk-warning-text-button"
                        variant="secondary"
                        :data-test-id="`zoppm-partneri-po-vytvorit-partnera`"
                        @click="handleCreate"
                      >
                        {{ t('zoppm.step18.vytvoritPartneraProjektu') }}
                      </IDSKButton>
                    </dl>
                  </section>
                </IDSKContainer>
              </template>

              <template v-if="isSectionAddPartnerVisible">
                <IDSKDetails
                  :title="t('zoppm.step18.zobrazitVyhladavaciFormular')"
                  @click="toggleNewPartnerSection"
                ></IDSKDetails>

                <IDSKHeading as="h1" variant="m">{{
                  t('zoppm.step18.zakladneUdajeFoPartner')
                }}</IDSKHeading>

                <ValidationWrapperMultiselect
                  v-slot="slotProps"
                  name="osobaForm[0].titulyPredMenom"
                  :value="titulPredMenomArrayForm"
                >
                  <IDSKSelect
                    id="titulyPredMenom"
                    v-bind="slotProps"
                    :label="t('globalne.titulyPredMenom')"
                    :options="titulPredMenom"
                    multiple
                  />
                </ValidationWrapperMultiselect>

                <ValidationWrapper v-slot="slotProps" name="osobaForm[0].meno">
                  <IDSKInput
                    id="osoba.meno"
                    v-bind="slotProps"
                    :label="t('globalne.meno')"
                    :disabled="true"
                  />
                </ValidationWrapper>
                <ValidationWrapper v-slot="slotProps" name="osobaForm[0].priezvisko">
                  <IDSKInput
                    id="osoba.priezvisko"
                    v-bind="slotProps"
                    :disabled="true"
                    :label="t('globalne.priezvisko')"
                  />
                </ValidationWrapper>

                <ValidationWrapperMultiselect
                  v-slot="slotProps"
                  name="osobaForm[0].titulyZaMenom"
                  :value="titulZaMenomArrayForm"
                >
                  <IDSKSelect
                    id="titulyZaMenom"
                    v-bind="slotProps"
                    :label="t('globalne.titulyZaMenom')"
                    :options="titulZaMenom"
                    multiple
                  />
                </ValidationWrapperMultiselect>
                <ValidationWrapper
                  v-slot="slotProps"
                  name="osobaForm[0].jeVerejnyCinitel"
                  :value="false"
                >
                </ValidationWrapper>
                <ValidationWrapper
                  v-slot="slotProps"
                  name="osobaForm[0].jeZahranicnaOsoba"
                  :value="false"
                >
                </ValidationWrapper>
                <ValidationWrapper v-slot="slotProps" name="osobaForm[0].rodneCislo">
                  <IDSKInput
                    v-bind="slotProps"
                    id="osoba.rodneCislo"
                    hint="Zadáva sa bez lomítka"
                    label="Rodné číslo"
                    class="govuk-input--width-10 mb-5"
                    :hint="t('globalne.rodneCisloHint')"
                    :disabled="showDatumNarodenia || undefined"
                  />
                </ValidationWrapper>

                <IDSKParagraph class="govuk-body-s">
                  {{ t('subjektOsoby.search.dateHint') }}
                </IDSKParagraph>
                <IDSKDetails
                  :title="t('subjektOsoby.search.dateSummaryTitle')"
                  class="mb-8"
                  :open="open"
                  @is-open="handleOpen"
                >
                  <IDSKFieldset>
                    <template #legend>{{ t('subjektOsoby.search.dateLegend') }}</template>
                    <template #fields>
                      <ValidationWrapper
                        v-slot="slotProps"
                        name="osobaForm[0].datumNarodenia"
                      >
                        <IDSKDateInputSeparated
                          id="osoba.datumNarodenia"
                          v-bind="slotProps"
                          v-model="datumNarodeniaForm"
                          :mode="modeDate"
                        />
                      </ValidationWrapper>
                    </template>
                  </IDSKFieldset>
                </IDSKDetails>

                <div class="govuk-!-width-one-half">
                  <ValidationWrapper
                    v-slot="slotProps"
                    name="osobaForm[0].statnaPrislusnost"
                    :value="statnaPrislusnost"
                  >
                    <IDSKSelect
                      id="statnaPrislusnost"
                      v-bind="slotProps"
                      :label="t('globalne.statnaPrislusnost')"
                      :options="stat"
                      full-width
                    />
                  </ValidationWrapper>
                </div>
                <br />
                <br />
                <IDSKHeading as="h3" variant="m">
                  {{ t('adresnyFormular.kontaktnaAdresa') }}
                </IDSKHeading>
                <span class="govuk-hint">{{ t('zoppm.step18.zadajteAdresu') }}</span>
                <AddressFieldsPartneri
                  field-name-prefix="osobaForm[0].primarnaAdresa"
                  :is-locked="false"
                  :sk-predefined="skPredefined"
                  :kraj-selection="krajSelection"
                  :okres-selection="okresSelection"
                  :obec-selection="obecSelection"
                  :ulica-selection="ulicaSelection"
                />
                <IDSKFieldset class="mb-4">
                  <template #legend>
                    <IDSKHeading as="h2" variant="m" class="mb-3">
                      {{ t('zoppm.step18.kontakt') }}
                    </IDSKHeading>
                    <span class="govuk-hint">
                      {{ t('zoppm.step18.zadajtePK') }}
                    </span>
                  </template>
                  <template #fields>
                    <ValidationWrapper
                      v-slot="slotProps"
                      name="osobaForm[0].primarnyKontakt.email"
                      value="@"
                    >
                      <IDSKInput
                        id="emailNaSubjekte"
                        v-bind="slotProps"
                        :label="t('globalne.email')"
                      />
                    </ValidationWrapper>
                    <ValidationWrapper
                      v-slot="slotProps"
                      name="osobaForm[0].primarnyKontakt.telefonneCislo"
                      value="+421"
                    >
                      <IDSKInput
                        id="telefonneCisloNaSubjekte"
                        v-bind="slotProps"
                        :label="t('globalne.telefonickyKontakt')"
                        class="govuk-input--width-10"
                      />
                    </ValidationWrapper>
                  </template>
                </IDSKFieldset>
                <IDSKButtonGroup class="mb-0">
                  <IDSKButton
                    v-if="vytvoritNovehoPartnera"
                    :data-test-id="`zoppm-partneri-po-vytvorit-noveho-partnera`"
                    @click="createNewPartner"
                  >
                    {{ t('zoppm.step18.vytvoritNovehoPartneraProjektu') }}
                  </IDSKButton>

                  <IDSKButton
                    v-if="aktualizovatPartnera"
                    :data-test-id="`zoppm-partneri-po-aktualizovat-partnera`"
                    @click="aktualizovatPartneraFo"
                  >
                    {{ t('zoppm.step18.aktualizovatPartneraProjektu') }}
                  </IDSKButton>
                  <IDSKButton
                    v-if="odstranitPartnera"
                    variant="warning"
                    :data-test-id="`zoppm-partneri-po-odobrat-partnera-projektu`"
                    @click="odobratPartneraAktualizovanyOsoba"
                  >
                    {{ t('zoppm.step18.odobratPartneraProjektu') }}
                  </IDSKButton>
                  <IDSKButton
                    variant="secondary"
                    :data-test-id="`zoppm-partneri-po-zavriet-formular`"
                    @click="toggleNewPartnerSection"
                  >
                    {{ t('zoppm.step18.zavrietFormular') }}
                  </IDSKButton>
                </IDSKButtonGroup>
              </template>
              <template v-if="isSectionShowDetailPartnerVisible">
                <DisplayList
                  bold="title"
                  variant="inline"
                  inline-layout="one-half"
                  class="mb-8"
                >
                  <dt>{{ t('zoppm.step18.identifikatorISPO') }}</dt>
                  <dd>
                    {{ detailOsoba.osobaId }}
                  </dd>
                  <dt>{{ t('zoppm.step18.menoPartneraProjektu') }}</dt>
                  <dd>
                    {{ detailOsoba.menoUplne }}
                  </dd>
                  <dt>{{ t('zoppm.step18.datumNarodenia') }}</dt>
                  <dd>
                    {{ detailOsoba.datumNarodenia }}
                  </dd>

                  <dt style="margin-top: 20px">
                    {{ t('zoppm.step18.adresaSidlaZiadatela') }}
                  </dt>
                  <dd style="margin-top: 20px">
                    {{ detailOsoba.adresaUplna }}
                  </dd>
                </DisplayList>
                <IDSKButton
                  variant="warning"
                  :data-test-id="`zoppm-partneri-po-odobrat-partnera`"
                  @click="odobratPartneraOsoba(detailOsoba)"
                >
                  {{ t('zoppm.step18.odobratPartneraProjektu') }}
                </IDSKButton>
              </template>
            </div>
            <div class="govuk-radios__item">
              <input
                id="PoRadio"
                class="govuk-radios__input"
                name="partneriRadio"
                type="radio"
                value="PO"
                aria-expanded="false"
                :checked="PoRadioChecked"
                @change="toggleSectionRadioPO"
              />
              <label class="govuk-label govuk-radios__label">
                {{ t('zoppm.step18.pravnickaOsoba') }}
              </label>
            </div>
            <div v-if="isPOVisible" class="govuk-radios__conditional">
              <template v-if="isSectionSearchPartnerVisible">
                <IDSKErrorSummary
                  v-if="state.showFillError"
                  :title="t('globalne.chyba')"
                  :description="t('subjektOsoby.search.errorEmptyInputs')"
                  class="mb-6"
                />
                <section>
                  <IDSKRow>
                    <IDSKCol variant-desktop="one-half">
                      <ValidationWrapper v-slot="slotProps" name="organizaciaForm[0].ico">
                        <IDSKInput
                          v-bind="slotProps"
                          id="ico"
                          v-model="subjektIco"
                          type="number"
                          label="IČO"
                        />
                      </ValidationWrapper>

                      <ValidationWrapper v-slot="slotProps" name="organizaciaForm[0].dic">
                        <IDSKInput
                          v-bind="slotProps"
                          id="organizacia.dic"
                          v-model="subjektDic"
                          type="number"
                          label="DIČ"
                        />
                      </ValidationWrapper>
                    </IDSKCol>
                  </IDSKRow>
                  <br />
                  <IDSKButton
                    variant="secondary"
                    :data-test-id="`zoppm-partneri-po-vyhladat-partnera`"
                    @click="searchSubjekty()"
                  >
                    {{ t('zoppm.step18.vyhladatPartneraProjektu') }}
                  </IDSKButton>

                  <section v-if="state.isLoading">
                    <IDSKParagraph>Načítavanie...</IDSKParagraph>
                  </section>
                  <section v-else-if="state.firstLoad && foundSubjekty">
                    <IDSKParagraph class="mb-3">
                      {{ t('zoppm.step18.vysledkyPreIco') }}
                      <span v-if="searchValuesPo.subjektIco">
                        <strong> IČO {{ searchValuesPo.subjektIco }} </strong>
                        <span v-if="searchValuesPo.subjektDic"
                          >, DIČ <strong>{{ searchValuesPo.subjektDic }} </strong>:
                        </span>
                      </span>
                      <span v-else-if="searchValuesPo.subjektDic">
                        DIČ <strong>{{ searchValuesPo.subjektDic }} </strong>:
                      </span>

                      <br />
                      {{ t('subjektOsoby.search.searchSummaryPo', replaceResultsTextPo) }}
                    </IDSKParagraph>
                    <IDSKDivider class="mb-6" />

                    <template v-if="foundSubjekty?.length === 0">
                      <IDSKErrorSummary :title="t('zoppm.step18.nenaslaSaOrganizacia')">
                        <template #description>
                          {{ t('zoppm.step18.nevyhovujeKriteriam') }}<br /><br />
                          {{ t('zoppm.step18.vytvorSiNovu') }}
                        </template>
                      </IDSKErrorSummary>
                      <IDSKButton
                        :data-test-id="`zoppm-partneri-po-vytvorit-partnera`"
                        @click="handleCreate"
                      >
                        {{ t('zoppm.step18.vytvoritPartneraProjektu') }}
                      </IDSKButton>
                    </template>
                    <dl v-if="foundSubjekty?.length > 0">
                      <template v-for="(item, id) in foundSubjekty" :key="id">
                        <dt>
                          <strong>
                            {{ item?.nazov }}
                          </strong>
                        </dt>
                        <dd class="ml-0">
                          <span class="govuk-hint">
                            {{ item?.adresaUplna }}
                          </span>
                          <br />
                          <IDSKButton
                            :data-test-id="`zoppm-partneri-po-vybrat-partnera`"
                            @click="() => handleAddOrganizacia(item)"
                          >
                            {{ t('zoppm.step18.vybratPartneraProjektu') }}
                          </IDSKButton>
                        </dd>
                      </template>
                      <IDSKWarningTextCustom variant="warningInfo" class="mb-4 pr-2">
                        {{ t('zoppm.step18.formularJeUkazkovy') }}
                      </IDSKWarningTextCustom>
                      <IDSKButton
                        v-if="!searchCountIsEmpty"
                        class="govuk-warning-text-button"
                        variant="secondary"
                        :data-test-id="`zoppm-partneri-po-vytvorit-partnera`"
                        @click="handleCreate"
                      >
                        {{ t('zoppm.step18.vytvoritPartneraProjektu') }}
                      </IDSKButton>
                    </dl>
                  </section>
                </section>
              </template>
              <template v-if="isSectionAddPartnerVisible">
                <br />
                <IDSKErrorSummary
                  v-if="statePo.showFillErrorPartner"
                  :title="t('globalne.chyba')"
                  :description="t('zoppm.step18.chybaNazovSubjektu')"
                  class="mb-6"
                />

                <IDSKDetails
                  :title="t('zoppm.step18.zobrazitVyhladavaciFormular')"
                  @click="toggleNewPartnerSection"
                ></IDSKDetails>
                <IDSKHeading as="h3" variant="m">
                  {{ t('zoppm.step18.zakladneUdajePartneraPO') }}
                </IDSKHeading>

                <ValidationWrapper v-slot="slotProps" name="organizaciaForm[0].ico">
                  <IDSKInput v-bind="slotProps" id="ico" label="IČO" class="w-50" />
                </ValidationWrapper>

                <ValidationWrapper v-slot="slotProps" name="organizaciaForm[0].nazov">
                  <IDSKInput
                    v-bind="slotProps"
                    id="subjekt.organizacia.nazov"
                    label="Názov subjektu"
                    aria-required="true"
                  />
                </ValidationWrapper>

                <ValidationWrapper
                  v-slot="slotProps"
                  name="organizaciaForm[0].pravnaForma"
                  value=""
                >
                  <IDSKSelect
                    id="pravnaForma"
                    v-bind="slotProps"
                    full-width
                    :label="t('pouzivatel.pravnaForma')"
                    :options="pravnaForma"
                  />
                </ValidationWrapper>
                <ValidationWrapper v-slot="slotProps" name="organizaciaForm[0].dic">
                  <IDSKInput
                    v-bind="slotProps"
                    id="organizacia.dic"
                    class="govuk-input--width-20"
                    :label="t('pouzivatel.dic')"
                  />
                </ValidationWrapper>
                <ValidationWrapper
                  v-slot="{ 'onUpdate:modelValue': update, modelValue, ...slotProps }"
                  name="organizaciaForm[0].jePlatcaDph"
                  :value="false"
                >
                  <IDSKCheckbox
                    id="jePlatcaDph"
                    v-bind="slotProps"
                    :checked="modelValue"
                    :label="t('pouzivatel.platcaDph')"
                    @is-checked="(val) => update(val)"
                  >
                    <template #hidden-content>
                      <ValidationWrapper
                        v-slot="slotProps"
                        name="organizaciaForm[0].icDph"
                      >
                        <IDSKInput
                          v-bind="slotProps"
                          :label="t('pouzivatel.icDphVat')"
                          class="govuk-input--width-20"
                        />
                      </ValidationWrapper>
                    </template>
                  </IDSKCheckbox>
                </ValidationWrapper>
                <br />
                <IDSKHeading as="h3" variant="m">
                  {{ t('zoppm.step18.adresaSidla') }}
                </IDSKHeading>
                <AddressFieldsPartneri
                  field-name-prefix="organizaciaForm[0].sidlo"
                  :is-locked="false"
                  :sk-predefined="skPredefined"
                  :kraj-selection="krajSelection"
                  :okres-selection="okresSelection"
                  :obec-selection="obecSelection"
                  :ulica-selection="ulicaSelection"
                />
                <ValidationWrapper
                  v-slot="slotProps"
                  name="organizaciaForm[0].jeZahranicna"
                  :value="false"
                >
                </ValidationWrapper>
                <IDSKFieldset class="mb-4">
                  <template #legend>
                    <IDSKHeading as="h2" variant="m" class="mb-3">
                      {{ t('zoppm.step18.kontakt') }}
                    </IDSKHeading>
                    <IDSKParagraph>
                      {{ t('zoppm.step18.zadajtePK') }}
                    </IDSKParagraph>
                  </template>
                  <template #fields>
                    <ValidationWrapper
                      v-slot="slotProps"
                      name="organizaciaForm[0].primarnyKontakt.email"
                      value="@"
                    >
                      <IDSKInput
                        id="emailNaSubjekte"
                        v-bind="slotProps"
                        :label="t('globalne.email')"
                      />
                    </ValidationWrapper>
                    <ValidationWrapper
                      v-slot="slotProps"
                      name="organizaciaForm[0].primarnyKontakt.telefonneCislo"
                      value="+421"
                    >
                      <IDSKInput
                        id="telefonneCisloNaSubjekte"
                        v-bind="slotProps"
                        :label="t('globalne.telefonickyKontakt')"
                        class="govuk-input--width-20"
                      />
                    </ValidationWrapper>
                  </template>
                </IDSKFieldset>
                <IDSKButtonGroup class="mb-0">
                  <IDSKButton
                    v-if="vytvoritNovehoPartnera"
                    :data-test-id="`zoppm-partneri-po-vytvorit-noveho-partnera`"
                    @click="createNewPartnerPo"
                  >
                    {{ t('zoppm.step18.vytvoritNovehoPartneraProjektu') }}
                  </IDSKButton>

                  <IDSKButton
                    v-if="aktualizovatPartnera"
                    :data-test-id="`zoppm-partneri-po-aktualizovat-partnera`"
                    @click="aktualizovatPartneraPo"
                  >
                    {{ t('zoppm.step18.aktualizovatPartneraProjektu') }}
                  </IDSKButton>
                  <IDSKButton
                    v-if="odstranitPartnera"
                    variant="warning"
                    :data-test-id="`zoppm-partneri-po-vytvorit-odobrat-partnera`"
                    @click="odobratPartneraAktualizovanyOsobaPo"
                  >
                    {{ t('zoppm.step18.odobratPartneraProjektu') }}
                  </IDSKButton>

                  <IDSKButton
                    variant="secondary"
                    :data-test-id="`zoppm-partneri-po-vytvorit-zavriet-formular`"
                    @click="toggleNewPartnerSection"
                  >
                    {{ t('zoppm.step18.zavrietFormular') }}
                  </IDSKButton>
                </IDSKButtonGroup>
              </template>
              <template v-if="isSectionShowDetailPartnerVisiblePo">
                <DisplayList
                  bold="title"
                  variant="inline"
                  inline-layout="one-half"
                  class="mb-8"
                >
                  <dt>{{ t('zoppm.step18.identifikatorISPO') }}</dt>
                  <dd>
                    {{ detailOrganizacia.organizaciaId }}
                  </dd>
                  <dt>{{ t('zoppm.step18.nazovZiadatela') }}</dt>
                  <dd>
                    {{ detailOrganizacia.nazov }}
                  </dd>
                  <dt>{{ t('zoppm.step18.ico') }}</dt>
                  <dd>
                    {{ detailOrganizacia.ico }}
                  </dd>
                  <dt>{{ t('zoppm.step18.dic') }}</dt>
                  <dd>
                    {{ detailOrganizacia.dic }}
                  </dd>
                  <dt>{{ t('zoppm.step18.platitelDph') }}</dt>
                  <dd>
                    <a v-if="detailOrganizacia.jePlatcaDph"> Áno </a>
                    <a v-else> Nie </a>
                  </dd>
                  <dt>{{ t('zoppm.step18.icDph') }}</dt>
                  <dd>
                    {{ detailOrganizacia.icDph }}
                  </dd>
                  <dt>{{ t('zoppm.step18.pravnaForma') }}</dt>
                  <dd>{{ detailOrganizacia.pravnaForma.nazov }}</dd>

                  <dt style="margin-top: 20px">
                    {{ t('zoppm.step18.adresaSidlaZiadatela') }}
                  </dt>
                  <dd style="margin-top: 20px">
                    {{ detailOrganizacia.sidlo.adresaUplna }}
                  </dd>
                </DisplayList>
                <IDSKButton
                  variant="warning"
                  :data-test-id="`zoppm-partneri-po-vytvorit-odobrat-partnera`"
                  @click="odobratPartneraOrganizacia(detailOrganizacia)"
                >
                  {{ t('zoppm.step18.odobratPartneraProjektu') }}
                </IDSKButton>
              </template>
            </div>
          </div>
        </div>
      </fieldset>
    </section>
  </IDSKContainer>
</template>

<style lang="scss"></style>

